import { useEffect, useRef, useState, type ReactNode } from 'react';

import { handleEngineEvent } from '../engine/events.js';
import {
  createEngineView,
  registerView,
  retryPrism,
  useEngineStatus,
  type EngineView,
  type PrismMode,
} from '../engine/prism.js';
import { readProjectCache, readProjectFile, writeProjectCache } from '../engine/host.js';
import { currentGameAccount, gameAccount } from '../engine/account.js';
import { projectSaveHost, type SaveHost } from '../engine/saves.js';
import { logEditor } from '../state/console.js';
import { useWorkspace } from '../state/workspace.js';
import { Icon } from './ui/icons.js';

/** A crashed view restarts by itself this long after the crash... */
const AUTO_RESTART_DELAY_MS = 1500;
/** ...at most this many times per mode in this window (then the restart button). */
const AUTO_RESTART_LIMIT = 3;
const AUTO_RESTART_WINDOW_MS = 120_000;
const autoRestarts = new Map<PrismMode, number[]>();

/** Whether a crashed `mode` view may restart by itself now (records the restart). */
function takeAutoRestart(mode: PrismMode): boolean {
  const now = Date.now();
  const recent = (autoRestarts.get(mode) ?? []).filter((at) => now - at < AUTO_RESTART_WINDOW_MS);
  if (recent.length >= AUTO_RESTART_LIMIT) {
    autoRestarts.set(mode, recent);
    return false;
  }
  autoRestarts.set(mode, [...recent, now]);
  return true;
}

/**
 * A canvas hosting one Prism engine view.
 *
 * Owns the view's lifecycle (create → openProject → `onReady` → dispose),
 * keeps it sized to the panel, and forwards DOM input when the engine asks
 * for it. When the engine bundle is not built it renders `fallback` (or a
 * placeholder) instead, and the rest of the editor keeps working.
 *
 * The engine usually runs in the engine worker (engine/engine-bridge.ts):
 * the canvas is handed over as an OffscreenCanvas, so every view gets a
 * fresh canvas element (a transferred one can never be drawn by the page
 * again), and the page forwards what a worker cannot see itself: size and
 * visibility, input, gamepads and the audio output.
 */
export function Viewport({
  mode,
  onReady,
  fallback,
  children,
  className = '',
  instanceKey = 0,
  source,
}: {
  mode: PrismMode;
  /** Runs once the view exists and the project is open (open a scene/model, play...). */
  onReady?: (view: EngineView) => void | Promise<void>;
  /** Shown when the engine is unavailable. */
  fallback?: ReactNode;
  /** Overlays (toolbars, stats) drawn above the canvas. */
  children?: ReactNode;
  className?: string;
  /** Change to recreate the view (e.g. restart Play). */
  instanceKey?: number;
  /**
   * Run something other than the open project (the website's game library
   * player): its files come from `readFile`; nothing is written back and saves
   * stay in the browser.
   */
  source?: { key: string; readFile(path: string): Promise<Uint8Array>; saves?: SaveHost };
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  /** Holds the canvas of the current view (created per view, see below). */
  const slotRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<EngineView | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const workspaceProject = useWorkspace((s) => s.projectId);
  const projectId = source ? null : workspaceProject;
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const status = useEngineStatus((s) => s.status);
  const [live, setLive] = useState(false);
  const [retry, setRetry] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  /** Play mode has no camera entity rendering the game (engine `noCamera` event). */
  const [noCamera, setNoCamera] = useState(false);
  /** Game scripts asked for the mouse to be captured (`set_cursor_locked`). */
  const wantLockRef = useRef(false);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot || (!projectId && !sourceRef.current)) return;
    const external = sourceRef.current;
    // A fresh canvas per view: the engine worker takes it over for good.
    const canvas = document.createElement('canvas');
    canvas.className = 'viewport-canvas';
    canvas.tabIndex = 0;
    canvas.dataset.mode = mode;
    const box = hostRef.current?.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (box && box.width >= 1 && box.height >= 1) {
      canvas.width = Math.round(box.width * dpr);
      canvas.height = Math.round(box.height * dpr);
    }
    slot.replaceChildren(canvas);
    canvasRef.current = canvas;
    let disposed = false;
    let view: EngineView | null = null;
    let restartTimer: ReturnType<typeof setTimeout> | null = null;
    setLive(false);
    setFailure(null);
    setNoCamera(false);

    void createEngineView(
      canvas,
      mode,
      {
        ...(external || projectId === null
          ? {
              readFile: (path: string) => external!.readFile(path),
              // A game from the website's arcade keeps its saves in this browser (see arcadeSaveHost).
              ...(mode === 'play' && external?.saves ? external.saves : {}),
            }
          : {
              readFile: (path: string) => readProjectFile(projectId, path),
              // Built models etc.: stored in .prism/cache/ so the next load skips the work.
              writeCache: (path: string, bytes: Uint8Array) => writeProjectCache(projectId, path, bytes),
              readCache: (path: string) => readProjectCache(projectId, path),
              // Play mode: save_game/load_game persist in the project's .prism/saves/.
              ...(mode === 'play' ? projectSaveHost(projectId) : {}),
            }),
        // Games that use Prism accounts see the signed-in person (Play only).
        account: mode === 'play' ? currentGameAccount() : null,
        onEvent: (json) => {
          if (json.includes('"rumble"')) playRumble(json);
          if (json.includes('"cursorLock"')) applyCursorLock(canvas, wantLockRef, json);
          if (json.includes('"cursorVisible"')) applyCursorVisible(canvas, json);
          if (json.includes('"noCamera"')) {
            const active = noCameraActive(json);
            if (active !== null && !disposed) setNoCamera(active);
          }
          handleEngineEvent(mode, json);
        },
      },
      (message) => logEditor('error', `[${mode}] ${message}`),
    )
      .then(async (created) => {
        if (!created) return;
        if (disposed) {
          created.dispose();
          return;
        }
        view = created;
        viewRef.current = created;
        if (mode === 'play') void gameAccount().then((a) => !disposed && created.setAccount(a));
        // A panic kills this view: show it once (with the panic message) and
        // restart it by itself (a new engine worker), so nobody has to reload
        // the page. A crash loop falls back to the restart button.
        created.onCrash = (message) => {
          if (disposed) return;
          setFailure(message);
          if (takeAutoRestart(mode)) {
            logEditor('warn', `[${mode}] The engine crashed and restarts by itself: ${message}`);
            restartTimer = setTimeout(() => {
              if (disposed) return;
              retryPrism();
              setRetry((n) => n + 1);
            }, AUTO_RESTART_DELAY_MS);
          } else {
            logEditor('error', `[${mode}] The engine crashed ${AUTO_RESTART_LIMIT} times in ${AUTO_RESTART_WINDOW_MS / 60_000} minutes; use Restart in the viewport.`);
          }
        };
        registerView(mode, created);
        const host = hostRef.current;
        if (host) {
          const r = host.getBoundingClientRect();
          created.resize(Math.round(r.width), Math.round(r.height), window.devicePixelRatio || 1);
        }
        await created.openProject();
        if (disposed) return;
        if (created.error) throw new Error(created.error);
        await onReadyRef.current?.(created);
        if (created.error) throw new Error(created.error);
        if (!disposed) setLive(true);
      })
      .catch((error: unknown) => {
        if (disposed) return;
        const message = error instanceof Error ? error.message : String(error);
        setFailure(message);
        registerView(mode, null);
        view?.dispose();
        viewRef.current = null;
      });

    return () => {
      disposed = true;
      if (restartTimer) clearTimeout(restartTimer);
      wantLockRef.current = false;
      canvas.style.cursor = '';
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      if (view) {
        registerView(mode, null);
        view.dispose();
      }
      viewRef.current = null;
      if (canvasRef.current === canvas) canvasRef.current = null;
      canvas.remove();
    };
  }, [mode, projectId, instanceKey, retry, source?.key]);

  // Keep the engine's surface matched to the panel (the engine sizes the
  // canvas itself: a canvas in the engine worker cannot be resized here).
  // A panel with no size (hidden tab, collapsed dock) stops drawing.
  //
  // The size is checked every animation frame, like the engine used to do
  // with the canvas when it ran on the page: a ResizeObserver alone missed
  // panels that were created hidden and shown later (the view stayed at its
  // first size, drawing nothing). Only changes are sent to the engine.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !live) return;
    let frame = 0;
    let last = '';
    const sync = () => {
      const view = viewRef.current;
      const w = host.clientWidth;
      const h = host.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      const key = `${w}x${h}@${dpr}`;
      if (view && key !== last) {
        last = key;
        const visible = w >= 1 && h >= 1;
        view.setVisible(visible);
        if (visible) view.resize(w, h, dpr);
      }
    };
    const loop = () => {
      sync();
      frame = requestAnimationFrame(loop);
    };
    loop();
    const observer = new ResizeObserver(sync);
    observer.observe(host);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [live]);

  // The engine worker has no Gamepad API: Play views get the pads from here.
  useEffect(() => {
    const view = viewRef.current;
    if (!live || mode !== 'play' || !view?.inWorker || typeof navigator.getGamepads !== 'function') return;
    let frame = 0;
    let last = '';
    const poll = () => {
      const json = gamepadsJson();
      if (json !== last) {
        last = json;
        view.setGamepads(json);
      }
      frame = requestAnimationFrame(poll);
    };
    poll();
    return () => cancelAnimationFrame(frame);
  }, [live, mode]);

  // Forward input only to engines that ask for it (they implement `input`).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !live) return;
    const view = viewRef.current;
    if (!view?.acceptsInput) return;
    canvas.style.touchAction = 'none'; // touch / pen deliver pointermove instead of scrolling
    // Phones: no long-press menus, text selection or tap flashes on the game.
    canvas.style.setProperty('-webkit-touch-callout', 'none');
    canvas.style.setProperty('-webkit-user-select', 'none');
    canvas.style.userSelect = 'none';
    canvas.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    // The safe area (notch, rounded corners, home bar): on-screen touch controls stay inside it.
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
    document.body.appendChild(probe);
    const sendSafeArea = () => {
      const cs = getComputedStyle(probe);
      view.setSafeAreaInsets(parseFloat(cs.paddingTop) || 0, parseFloat(cs.paddingRight) || 0, parseFloat(cs.paddingBottom) || 0, parseFloat(cs.paddingLeft) || 0);
    };
    sendSafeArea();
    window.addEventListener('resize', sendSafeArea);
    screen.orientation?.addEventListener?.('change', sendSafeArea);
    const rel = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const mods = (e: MouseEvent | KeyboardEvent) => ({
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      alt: e.altKey,
    });
    const pointer = (kind: string) => (e: PointerEvent) => {
      if (kind === 'pointerdown') {
        // Capture can be refused (the pointer is no longer active, pointer lock, a synthetic event):
        // the press must still reach the game, or the click is lost.
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* no capture: the press still goes to the game */
        }
        canvas.focus();
        view.resumeAudio(); // browsers keep audio suspended until a gesture
      }
      view.input({
        kind,
        ...rel(e),
        button: e.button,
        buttons: e.buttons,
        dx: e.movementX,
        dy: e.movementY,
        pointerType: e.pointerType,
        pointerId: e.pointerId,
        pressure: e.pressure,
        tiltX: e.tiltX,
        tiltY: e.tiltY,
        ...mods(e),
      });
    };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      view.input({ kind: 'wheel', ...rel(e), deltaY: e.deltaY, ...mods(e) });
    };
    const key = (kind: string) => (e: KeyboardEvent) => {
      if (kind === 'keydown') view.resumeAudio();
      view.input({ kind, key: e.key, code: e.code, ...mods(e) });
    };
    const handlers: Array<[string, EventListener]> = [
      ['pointerdown', pointer('pointerdown') as EventListener],
      ['pointermove', pointer('pointermove') as EventListener],
      ['pointerup', pointer('pointerup') as EventListener],
      ['wheel', wheel as EventListener],
      ['keydown', key('keydown') as EventListener],
      ['keyup', key('keyup') as EventListener],
      ['blur', (() => view.input({ kind: 'blur' })) as EventListener],
      // A cancelled finger ends just that finger (the engine releases its control).
      ['pointercancel', pointer('pointercancel') as EventListener],
      ['contextmenu', ((e: Event) => e.preventDefault()) as EventListener],
    ];
    for (const [type, fn] of handlers)
      canvas.addEventListener(type, fn, { passive: type !== 'wheel' });
    // Browsers only grant pointer lock on a user gesture: re-capture on click
    // while the game wants it (Escape releases it).
    const relock = () => {
      if (wantLockRef.current && document.pointerLockElement !== canvas) requestLock(canvas);
    };
    const lockChange = () => {
      // A lock is granted later than asked (browsers resolve requestPointerLock asynchronously): when
      // the game freed the mouse in between (a menu or the character creator opened while the world
      // started), the late lock would trap the cursor and nothing on screen could be clicked.
      if (document.pointerLockElement === canvas && !wantLockRef.current) {
        document.exitPointerLock();
        return;
      }
      view.input({ kind: 'pointerLock', locked: document.pointerLockElement === canvas });
    };
    canvas.addEventListener('pointerdown', relock);
    document.addEventListener('pointerlockchange', lockChange);
    return () => {
      for (const [type, fn] of handlers) canvas.removeEventListener(type, fn);
      canvas.removeEventListener('pointerdown', relock);
      document.removeEventListener('pointerlockchange', lockChange);
      window.removeEventListener('resize', sendSafeArea);
      screen.orientation?.removeEventListener?.('change', sendSafeArea);
      probe.remove();
    };
  }, [live]);

  const unavailable = Boolean(failure) || status.state === 'missing' || status.state === 'error';
  const errorMessage = failure ?? (status.state === 'error' ? status.message : undefined);

  return (
    <div className={`viewport${live ? ' is-live' : ''} ${className}`} ref={hostRef}>
      <div ref={slotRef} className="viewport-canvas-slot" />
      {!live && (
        <div className="viewport-fallback">
          {unavailable && fallback ? (
            fallback
          ) : (
            <EnginePlaceholder
              loading={!unavailable}
              message={errorMessage}
              onRetry={() => {
                retryPrism();
                setRetry((n) => n + 1);
              }}
            />
          )}
        </div>
      )}
      {unavailable && !live && fallback && (
        <EngineBadge
          message={errorMessage}
          onRetry={() => {
            retryPrism();
            setRetry((n) => n + 1);
          }}
        />
      )}
      {live && noCamera && (
        <div className="viewport-no-camera" role="status">
          <strong>No camera rendering</strong>
          <span>Add a Camera component to an entity in the scene (or spawn one from a script).</span>
        </div>
      )}
      {children}
    </div>
  );
}

export function EnginePlaceholder({
  loading,
  message,
  onRetry,
}: {
  loading: boolean;
  message?: string;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <div className="engine-placeholder">
        <div className="spinner" aria-label="Starting engine" />
        <span className="muted">Starting Prism Engine Cloud…</span>
      </div>
    );
  }
  return (
    <div className="engine-placeholder">
      <div className="engine-placeholder-mark">
        <Icon name="logo" size={34} />
      </div>
      <strong>{message ? 'Could not start Prism Engine Cloud' : 'Engine runtime not built'}</strong>
      <span className="muted">
        {message ?? (
          <>
            Run <code>pnpm engine:build</code> to compile Prism Engine Cloud to WebAssembly.
          </>
        )}
      </span>
      <button type="button" className="ghost small" onClick={onRetry}>
        <Icon name="refresh" size={13} /> Retry
      </button>
    </div>
  );
}

/** Small corner notice used when a fallback view is drawn instead. */
function EngineBadge({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <div
      className="engine-badge"
      title={message ?? 'Engine runtime not built — run pnpm engine:build'}
    >
      <Icon name="warning" size={13} />
      <span>
        {message ? (
          'Prism Engine Cloud needs attention — check the console'
        ) : (
          <>
            Engine runtime not built — run <code>pnpm engine:build</code>
          </>
        )}
      </span>
      <button type="button" onClick={onRetry} title="Retry loading the engine">
        <Icon name="refresh" size={12} />
      </button>
    </div>
  );
}

/** The connected pads, standard mapping (`PrismView.setGamepads` JSON). */
function gamepadsJson(): string {
  const pads: { index: number; id: string; buttons: number[]; axes: number[] }[] = [];
  for (const g of navigator.getGamepads()) {
    if (!g?.connected) continue;
    // Some digital buttons report pressed with value 0.
    const buttons = g.buttons.map((b) => (b.pressed && b.value <= 0 ? 1 : b.value));
    pads.push({ index: g.index, id: g.id, buttons, axes: [...g.axes] });
  }
  return JSON.stringify(pads);
}

/** Plays a `rumble` engine event (engine worker) on the matching pads. */
function playRumble(json: string): void {
  let r: { type?: string; pad?: number | null; seconds?: number; strong?: number; weak?: number };
  try {
    r = JSON.parse(json) as typeof r;
  } catch {
    return;
  }
  if (r.type !== 'rumble' || typeof navigator.getGamepads !== 'function') return;
  for (const g of navigator.getGamepads()) {
    if (!g?.connected || (r.pad != null && r.pad !== g.index)) continue;
    const actuator = (g as Gamepad & { vibrationActuator?: { playEffect?: (type: string, params: object) => Promise<unknown> } }).vibrationActuator;
    void actuator
      ?.playEffect?.('dual-rumble', { duration: (r.seconds ?? 0) * 1000, strongMagnitude: r.strong ?? 0, weakMagnitude: r.weak ?? 0 })
      .catch(() => undefined);
  }
}

function requestLock(canvas: HTMLCanvasElement): void {
  try {
    // Returns a promise in current browsers; rejects without a user gesture.
    const result = canvas.requestPointerLock() as unknown;
    if (result instanceof Promise) result.catch(() => {});
  } catch {
    // Not allowed right now; the next click into the game retries.
  }
}

/** Applies a `cursorLock` engine event (game scripts capturing/freeing the mouse). */
function applyCursorLock(
  canvas: HTMLCanvasElement,
  want: { current: boolean },
  json: string,
): void {
  let locked: boolean;
  try {
    const event = JSON.parse(json) as { type?: string; locked?: boolean };
    if (event.type !== 'cursorLock') return;
    locked = Boolean(event.locked);
  } catch {
    return;
  }
  want.current = locked;
  if (locked) {
    if (document.pointerLockElement !== canvas) requestLock(canvas);
  } else if (document.pointerLockElement === canvas) {
    document.exitPointerLock();
  }
}

/** Applies a `cursorVisible` engine event (scripts' `set_cursor_visible`). */
/** `active` of a `{"type":"noCamera","active":bool}` engine event, else null. */
function noCameraActive(json: string): boolean | null {
  try {
    const event = JSON.parse(json) as { type?: string; active?: boolean };
    return event.type === 'noCamera' ? event.active === true : null;
  } catch {
    return null;
  }
}

function applyCursorVisible(canvas: HTMLCanvasElement, json: string): void {
  try {
    const event = JSON.parse(json) as { type?: string; visible?: boolean };
    if (event.type !== 'cursorVisible') return;
    canvas.style.cursor = event.visible === false ? 'none' : '';
  } catch {
    // Not a cursor event.
  }
}
